import { CircularProgress } from "@material-ui/core";
import React from "react";
import { Transition } from "react-transition-group";
import styled from "styled-components";

import { getCanvasBundle } from "js/canvas/index";
import { appVersion } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { FlexBox } from "js/react/components/LayoutGrid";
import { themeColors } from "js/react/sharedStyles";
import { $ } from "js/vendor";

const logger = getLogger(LogGroup.EDITOR);

const SkippedSlideOverlaySvg = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 20;
`;

const SkippedSlideOverlayContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 30;
`;

const SkippedSlideOverlay = () => (
    <SkippedSlideOverlayContainer>
        <SkippedSlideOverlaySvg viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="Skipped Slide">
            <line
                x1="100"
                y1="0"
                x2="0"
                y2="100"
                stroke="#e8950b"
                strokeWidth="3"
                strokeLinecap="round"
            />
        </SkippedSlideOverlaySvg>
    </SkippedSlideOverlayContainer>
);

const CanvasFrame = styled.div.attrs(props => ({
    style: {
        width: props.width,
        height: props.width / 1280 * 720,
        opacity: props.isSkippedSlide ? 0.5 : 1
    }
}))`
    position: relative;
    overflow: hidden;
    outline: ${props => props.selected ? `solid 3px ${themeColors.ui_blue}` : "solid 1px #666"};
    flex-shrink: 0;
`;

const SpinnerOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 40;
    pointer-events: none;
`;

const CanvasContainer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    background: #4c4e56;
    pointer-events: none;
    transition: opacity 300ms;
    opacity: ${props => props.state == "entered" ? 1 : 0};
`;

const ThumbnailContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;

    & > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
    }
`;

class SlideCanvasThumbnail extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            canvasRendered: false
        };

        this.canvasContainerRef = React.createRef();
    }

    componentDidMount() {
        const { slide } = this.props;

        this.renderCanvasPromise = this.renderCanvas();
    }

    async componentDidUpdate(prevProps) {
        const { width } = this.props;

        if (!this.renderCanvasPromise) {
            return;
        }

        if (prevProps.width !== width) {
            this.renderCanvasPromise.then(() => {
                this.slideCanvas.$el.css({ transform: `scale(${width / this.slideCanvas.CANVAS_WIDTH})` });
            });
        }

        if (prevProps.theme !== this.props.theme && this.slideCanvas) {
            this.slideCanvas.options.theme = this.props.theme;
            await this.slideCanvas.renderModel(true);
        }
    }

    componentWillUnmount() {
        if (this.slideCanvas) {
            this.slideCanvas.remove();
        }
        if (this.readOnlySlide) {
            this.readOnlySlide.disconnect();
        }
    }

    renderCanvas = async () => {
        const { slide, width, theme } = this.props;

        try {
            const { SlideCanvas } = await getCanvasBundle(appVersion);

            this.readOnlySlide = slide.getReadOnlyClone();

            this.slideCanvas = new SlideCanvas({
                dataModel: this.readOnlySlide,
                editable: false,
                theme,
                isThumbnail: true
            });

            $(this.canvasContainerRef.current).append(this.slideCanvas.render().$el);

            this.slideCanvas.$el.css({ transform: `scale(${width / this.slideCanvas.CANVAS_WIDTH})` });

            await this.slideCanvas.renderSlide();

            this.setState({ canvasRendered: true });
        } catch (err) {
            logger.error(err, "[SlideThumbnail] renderCanvas failed", { slideId: slide.id });
        }
    }

    render() {
        const { canvasRendered } = this.state;

        return (
            <Transition in={canvasRendered} timeout={500}>
                {state => (
                    <CanvasContainer ref={this.canvasContainerRef} state={state} />
                )}
            </Transition>
        );
    }
}

class SlideImageThumbnail extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            thumbnailUrl: null
        };

        this.thumbnailUrlForChangeId = null;
    }

    componentDidMount() {
        const { slide } = this.props;

        this.setThumbnailUrl();

        slide.on("change:_changeId", this.handleSlideChange);
    }

    componentWillUnmount() {
        const { slide } = this.props;

        slide.off("change:_changeId", this.handleSlideChange);
    }

    componentDidUpdate(prevProps) {
        const { isCurrentSlide } = this.props;

        if (prevProps.isCurrentSlide && !isCurrentSlide) {
            this.setThumbnailUrl();
        }
        if (prevProps.theme !== this.props.theme) {
            this.setThumbnailUrl(true);
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.isCurrentSlide !== this.props.isCurrentSlide || nextProps.theme !== this.props.theme || nextState.thumbnailUrl !== this.state.thumbnailUrl;
    }

    handleSlideChange = () => {
        this.setThumbnailUrl();
    }

    setThumbnailUrl = async forceRefresh => {
        const { slide } = this.props;
        const { thumbnailUrl } = this.state;

        if (thumbnailUrl && this.thumbnailUrlForChangeId && this.thumbnailUrlForChangeId === slide.get("_changeId") && !forceRefresh) {
            return;
        }

        try {
            this.setState({ thumbnailUrl: null });
            const metadata = await PresentationEditorController.slidesMetadataLoadPromise;
            const thumbnailUrl = await slide.getThumbnailUrl(slide.presentation.id, "small", 0, true, metadata[slide.id]?.modifiedAt, forceRefresh);
            this.thumbnailUrlForChangeId = slide.get("_changeId");
            this.setState({ thumbnailUrl });
        } catch (err) {
            logger.error(err, "[SlideThumbnail] setThumbnailUrl failed", { slideId: slide.id });
        }
    }

    render() {
        const { thumbnailUrl } = this.state;

        return (
            <ThumbnailContainer>
                {thumbnailUrl && <img src={thumbnailUrl} />}
                {!thumbnailUrl &&
                    <FlexBox fill>
                        <CircularProgress size={35} thickness={5} style={{ color: themeColors.ui_blue }} />
                    </FlexBox>
                }
            </ThumbnailContainer>
        );
    }
}

class SlideThumbnail extends React.Component {
    constructor(props) {
        super(props);

        this.canvasFrameRef = React.createRef();

        this.state = {
            isVisible: false
        };

        this.canvasHasBeenShown = false;
    }

    async componentDidMount() {
        this.initializeObserver();
    }

    componentWillUnmount() {
        this.observer?.disconnect();
    }

    initializeObserver = () => {
        this.observer?.disconnect();

        this.observer = new IntersectionObserver(entries => {
            this.setState({ isVisible: entries[0].isIntersecting });
        }, {
            root: null,
            rootMargin: "0px",
        });

        this.observer.observe(this.canvasFrameRef.current);
    }

    render() {
        const {
            slide,
            selected,
            width,
            isSkippedSlide = false,
            currentSlide,
            theme,
            renderCanvas,
        } = this.props;
        const {
            isVisible
        } = this.state;

        return (
            <CanvasFrame
                ref={this.canvasFrameRef}
                className="canvas-frame"
                selected={selected}
                width={width}
                isSkippedSlide={isSkippedSlide}
            >
                <SlideImageThumbnail key={`${slide.id}-image`} slide={slide} isCurrentSlide={currentSlide === slide} theme={theme} />
                {isVisible && renderCanvas && <SlideCanvasThumbnail key={`${slide.id}-canvas`} slide={slide} width={width} theme={theme} />}
                {isSkippedSlide &&
                    <SkippedSlideOverlay />
                }
            </CanvasFrame>
        );
    }
}

export default SlideThumbnail;
